import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import BackupSection from "@/components/BackupSection";
import { useVocab } from "@/lib/vocab";
import { useRights } from "@/lib/rights";
import ChatImage from "@/components/ChatImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EXPENSE_COLORS, expenseColor } from "@/lib/helpers";
import { Upload, Loader2, Image as ImageIcon, Copy, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SWATCHES = [
  { name: "Obsidian / Or", primary: "220 20% 10%", accent: "43 65% 55%" },
  { name: "Marine / Cuivre", primary: "215 40% 18%", accent: "25 75% 55%" },
  { name: "Forêt / Laiton", primary: "155 30% 14%", accent: "45 55% 50%" },
  { name: "Bordeaux / Sable", primary: "345 45% 22%", accent: "35 45% 62%" },
];

// Hoisted to module scope so they are NOT recreated on every keystroke (prevents input focus loss).
const Section = ({ title, children }) => (
  <div className="bg-card border border-border rounded-2xl shadow-card p-6 space-y-4">
    <h3 className="font-display font-bold text-xl">{title}</h3>{children}
  </div>
);

// Partie facturation des Réglages (lot T5-0b) : seuls champs que la secrétaire du transport peut modifier (mêmes que
// metier.SECRETARY_SETTINGS_FIELDS ; le serveur refuse toute requête qui contient un autre champ).
const BILLING_FIELDS = ["company_name", "tagline", "logo_url", "primary_color", "accent_color", "address", "vat_number",
  "vat_regime", "default_vat_rate", "invoice_prefix", "quote_prefix", "currency", "legal_mention", "workshop_address",
  "phone", "email", "siret", "iban", "bic", "payment_payee", "payment_methods", "payment_terms", "closing_message",
  "expense_categories", "expense_payment_methods"];

// Délais des alertes du dispatch (transport, lot T5b) : mêmes limites que le serveur (metier.ALERT_SETTINGS) ; une
// case vide coupe sa règle.
const ALERT_FIELDS = [
  ["alert_unreceived_after_min", "Pas encore reçue : minutes après l'attribution", "1 à 1 440 min"],
  ["alert_unreceived_before_h", "Pas encore reçue : heures avant la prise en charge", "0,5 à 72 h"],
  ["alert_unassigned_before_h", "Sans chauffeur : heures avant la prise en charge", "0,5 à 72 h"],
];

// Prix des courses (transport, lot T2) : mêmes règles que le serveur (metier.PRICE_SETTINGS). Taux de TVA saisis en %,
// enregistrés en fraction (10 % → 0.1, comme le taux par défaut) ; listes saisies une par ligne.
const PRICE_NUMBERS = [
  ["night_pct", "Majoration de nuit (%)"], ["holiday_pct", "Dimanche et jours fériés (%)"],
  ["wait_free_airport_min", "Attente gratuite à l'aéroport (min)"], ["wait_free_other_min", "Attente gratuite ailleurs (min)"],
  ["wait_slice_min", "Tranche d'attente (min)"], ["wait_slice_price", "Prix d'une tranche entamée (€)"],
];
const pct = (v) => (v == null ? "" : String(Math.round(v * 10000) / 100).replace(".", ","));
const comma = (v) => (v == null ? "" : String(v).replace(".", ","));

const cleanRequests = (list) => (list || []).map((r) => ({ key: r.key, label: r.label || "", detail_hint: r.detail_hint || "" }));

const Field = ({ label, k, type = "text", form, set }) => (
  <div className="space-y-1.5"><Label>{label}</Label>
    <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} />
  </div>
);

export default function SettingsPage() {
  const { settings, setSettings } = useSettings();
  const voc = useVocab();
  const conciergerie = voc.business === "conciergerie"; // abonnements, carburant, récupération à domicile
  const { user } = useAuth();
  const billingOnly = !useRights().settingsWrite; // secrétaire du transport : partie facturation seulement (lot T5-0b)
  const [form, setForm] = useState(null);
  const [logoUp, setLogoUp] = useState(false);
  const [confirmVat, setConfirmVat] = useState(false);
  // Demandes spéciales (transport, lot T1b-2) : liste enregistrée (pour repérer les demandes retirées) et
  // question « L'enlever aussi des courses à venir ? » après l'enregistrement.
  const [savedRequests, setSavedRequests] = useState([]);
  const [askRemove, setAskRemove] = useState([]);
  const logoRef = useRef(null);
  const bookingUrl = `${window.location.origin}/book`;

  useEffect(() => {
    api.get("/settings/full").then((r) => {
      setForm({
        ...r.data,
        mission_types_text: (r.data.mission_types || []).join("\n"),
        booking_fields: r.data.booking_fields || [],
        registration_code: r.data.registration_code || "",
        vat_regime: r.data.vat_regime === "assujetti" ? "assujetti" : "franchise", // repli franchise
        // Formules d'abonnement : saisies en texte, converties en nombres à l'enregistrement.
        subscription_plans: (r.data.subscription_plans || []).map((p) => ({ label: p.label || "", visits: String(p.visits), unit_price: String(p.unit_price) })),
        // Compte carburant (lot 18) : saisis en texte, convertis en nombres à l'enregistrement.
        fuel_service_fee: String(r.data.fuel_service_fee ?? 10),
        fuel_alert_pct: String(r.data.fuel_alert_pct ?? 15),
        fuel_debours_text: r.data.fuel_debours_text || "",
        // Frais de société (lot 20) : catégories { label, color } et moyens de paiement de la société.
        expense_categories: (r.data.expense_categories || []).map((c) => ({ label: c.label || "", color: c.color || "gray" })),
        expense_payment_methods: [...(r.data.expense_payment_methods || [])],
        special_requests: cleanRequests(r.data.special_requests),
        // Prix des courses (transport, lot T2).
        vat_rate_transfer: pct(r.data.vat_rate_transfer), vat_rate_hourly: pct(r.data.vat_rate_hourly),
        vehicle_classes_text: (r.data.vehicle_classes || []).join("\n"), extra_holidays_text: (r.data.extra_holidays || []).join("\n"),
        ...Object.fromEntries(PRICE_NUMBERS.map(([k]) => [k, comma(r.data[k])])),
        // Délais des alertes (transport, lot T5b) : virgule française ; vide = alerte coupée.
        ...Object.fromEntries(ALERT_FIELDS.map(([k]) => [k, r.data[k] == null ? "" : String(r.data[k]).replace(".", ",")])),
      });
      setSavedRequests(cleanRequests(r.data.special_requests));
    });
  }, []);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return; setLogoUp(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", data.path); toast.success("Logo téléversé — pensez à enregistrer");
    } catch { toast.error("Échec du téléversement"); } finally { setLogoUp(false); }
  };

  const previewColors = (primary, accent) => {
    document.documentElement.style.setProperty("--accent", accent);
    set("primary_color", primary); set("accent_color", accent);
  };

  const setFieldFlag = (key, flag, val) => setForm((f) => ({
    ...f,
    booking_fields: f.booking_fields.map((bf) => bf.key === key ? { ...bf, [flag]: val, ...(flag === "enabled" && !val ? { required: false } : {}) } : bf),
  }));

  const setPlan = (i, k, v) => setForm((f) => ({ ...f, subscription_plans: f.subscription_plans.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const addPlan = () => setForm((f) => ({ ...f, subscription_plans: [...f.subscription_plans, { label: "", visits: "", unit_price: "" }] }));
  const rmPlan = (i) => setForm((f) => ({ ...f, subscription_plans: f.subscription_plans.filter((_, j) => j !== i) }));

  // Frais de société : les deux listes ne sont jamais vides (le dernier élément ne se retire pas).
  const setCat = (i, k, v) => setForm((f) => ({ ...f, expense_categories: f.expense_categories.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const addCat = () => setForm((f) => {
    const used = f.expense_categories.map((c) => c.color);
    const color = Object.keys(EXPENSE_COLORS).find((k) => !used.includes(k)) || "gray";
    return { ...f, expense_categories: [...f.expense_categories, { label: "", color }] };
  });
  const rmCat = (i) => setForm((f) => ({ ...f, expense_categories: f.expense_categories.filter((_, j) => j !== i) }));
  const setMethod = (i, v) => setForm((f) => ({ ...f, expense_payment_methods: f.expense_payment_methods.map((m, j) => (j === i ? v : m)) }));
  const addMethod = () => setForm((f) => ({ ...f, expense_payment_methods: [...f.expense_payment_methods, ""] }));
  const rmMethod = (i) => setForm((f) => ({ ...f, expense_payment_methods: f.expense_payment_methods.filter((_, j) => j !== i) }));

  const setRequest = (i, k, v) => setForm((f) => ({ ...f, special_requests: f.special_requests.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const addRequest = () => setForm((f) => ({ ...f, special_requests: [...f.special_requests, { key: "", label: "", detail_hint: "" }] }));
  const rmRequest = (i) => setForm((f) => ({ ...f, special_requests: f.special_requests.filter((_, j) => j !== i) }));

  const removeFromUpcoming = async () => {
    const keys = askRemove.map((r) => r.key);
    setAskRemove([]);
    try {
      const { data } = await api.post("/settings/special-requests/remove-upcoming", { keys });
      toast.success(data.updated ? `Retirée de ${data.updated} ${data.updated > 1 ? `${voc.missions} à venir` : `${voc.mission} à venir`}.` : `Aucune ${voc.mission} à venir ne l'avait.`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copyLink = () => { navigator.clipboard.writeText(bookingUrl); toast.success("Lien copié"); };

  const num = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;
  const lines = (t) => String(t || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    if (!billingOnly && !form.mission_types_text.split("\n").some((s) => s.trim())) {
      toast.error(`La liste des types de ${voc.mission} est vide : gardez au moins une prestation.`);
      return;
    }
    const plans = form.subscription_plans.map((p) => ({ label: p.label.trim(), visits: parseInt(p.visits, 10), unit_price: parseFloat(p.unit_price) }));
    if (!billingOnly && plans.some((p) => !(p.visits >= 1) || !(p.unit_price >= 0))) {
      toast.error("Formules d'abonnement : indiquez pour chacune un nombre de visites (au moins 1) et un prix d'une visite.");
      return;
    }
    const fuelFee = parseFloat(form.fuel_service_fee);
    const fuelPct = parseFloat(form.fuel_alert_pct);
    if (!billingOnly && (!(fuelFee >= 0) || !(fuelPct >= 0 && fuelPct <= 100))) {
      toast.error("Compte carburant : indiquez des frais de service (0 ou plus) et un seuil d'alerte entre 0 et 100 %.");
      return;
    }
    // Frais de société : un nom par ligne, sans doublon, au moins un élément dans chaque liste.
    const cats = form.expense_categories.map((c) => ({ label: c.label.trim(), color: c.color }));
    const methods = form.expense_payment_methods.map((m) => m.trim());
    const dup = (names) => names.find((n, i) => names.findIndex((m) => m.toLowerCase() === n.toLowerCase()) !== i);
    if (cats.some((c) => !c.label) || methods.some((m) => !m)) {
      toast.error("Frais de société : donnez un nom à chaque catégorie et à chaque moyen de paiement, ou retirez la ligne vide.");
      return;
    }
    if (!cats.length || !methods.length) {
      toast.error("Frais de société : gardez au moins une catégorie et au moins un moyen de paiement.");
      return;
    }
    const dupCat = dup(cats.map((c) => c.label));
    const dupMethod = dup(methods);
    if (dupCat || dupMethod) {
      toast.error(dupCat ? `Catégorie en double : « ${dupCat} ».` : `Moyen de paiement en double : « ${dupMethod} ».`);
      return;
    }
    // Demandes spéciales (transport) : un nom par ligne, sans doublon ; la liste peut être vide.
    const requests = form.special_requests.map((r) => ({ key: r.key, label: r.label.trim(), detail_hint: r.detail_hint.trim() }));
    if (!conciergerie && !billingOnly) {
      if (requests.some((r) => !r.label)) {
        toast.error("Demandes spéciales : donnez un nom à chaque demande, ou retirez la ligne vide.");
        return;
      }
      const dupRequest = dup(requests.map((r) => r.label));
      if (dupRequest) { toast.error(`Demande en double : « ${dupRequest} ».`); return; }
    }
    const payload = {
      company_name: form.company_name, tagline: form.tagline ?? "", logo_url: form.logo_url, primary_color: form.primary_color, accent_color: form.accent_color,
      address: form.address, vat_number: form.vat_number, iban: form.iban,
      default_vat_rate: parseFloat(form.default_vat_rate), vat_regime: form.vat_regime, invoice_prefix: form.invoice_prefix, quote_prefix: form.quote_prefix,
      currency: form.currency, mission_types: form.mission_types_text.split("\n").map((s) => s.trim()).filter(Boolean),
      registration_code: form.registration_code, booking_fields: form.booking_fields,
      legal_mention: form.legal_mention, home_pickup_fee: parseFloat(form.home_pickup_fee) || 0,
      workshop_address: form.workshop_address, phone: form.phone, email: form.email, siret: form.siret, bic: form.bic,
      payment_payee: form.payment_payee, payment_terms: form.payment_terms, closing_message: form.closing_message,
      payment_methods: form.payment_methods, subscription_plans: plans,
      fuel_service_fee: fuelFee, fuel_alert_pct: fuelPct, fuel_debours_text: form.fuel_debours_text,
      expense_categories: cats, expense_payment_methods: methods,
      ...(conciergerie ? {} : { special_requests: requests, dispatch_phone: form.dispatch_phone ?? "",
        // Prix des courses (lot T2) : TVA en % → fraction ; listes une par ligne.
        vat_rate_transfer: num(form.vat_rate_transfer) / 100, vat_rate_hourly: num(form.vat_rate_hourly) / 100,
        vehicle_classes: lines(form.vehicle_classes_text), extra_holidays: lines(form.extra_holidays_text),
        night_start: form.night_start, night_end: form.night_end, holiday_countries: form.holiday_countries,
        ...Object.fromEntries(PRICE_NUMBERS.map(([k]) => [k, String(form[k] ?? "").trim()])),
        // Délais des alertes (lot T5b) : "" = alerte coupée (le serveur lit aussi « 1,5 »).
        ...Object.fromEntries(ALERT_FIELDS.map(([k]) => [k, String(form[k] ?? "").trim()])) }),
    };
    // Secrétaire du transport (lot T5-0b) : seulement la partie facturation.
    const sent = billingOnly ? Object.fromEntries(Object.entries(payload).filter(([k]) => BILLING_FIELDS.includes(k))) : payload;
    try {
      const { data } = await api.patch("/settings", sent); setSettings(data); toast.success("Réglages enregistrés");
      if (!conciergerie && !billingOnly) {
        // Les nouvelles demandes reçoivent leur clé du serveur : on relit la liste enregistrée.
        const retirees = savedRequests.filter((r) => !requests.some((x) => x.key === r.key));
        const { data: full } = await api.get("/settings/full");
        set("special_requests", cleanRequests(full.special_requests));
        setSavedRequests(cleanRequests(full.special_requests));
        if (retirees.length) setAskRemove(retirees);
      }
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-overline text-accent">White-label</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Réglages entreprise</h1>
        {billingOnly && <p className="text-sm text-muted-foreground mt-2" data-testid="settings-billing-only">Partie facturation ; le reste est réglé par le Patron ou le dispatch.</p>}
      </div>

      <Section title="Identité de marque">
        <Field label="Nom de l'entreprise" k="company_name" form={form} set={set} />
        <div>
          <Field label="Slogan" k="tagline" form={form} set={set} />
          <p className="text-xs text-muted-foreground mt-1">Affiché sous le nom de l'entreprise sur les factures et devis PDF (en petites capitales). Laissez vide pour ne rien afficher.</p>
        </div>
        <div className="space-y-2">
          <Label>Logo de l'entreprise</Label>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-xl bg-muted border border-border grid place-items-center overflow-hidden shrink-0">
              {form.logo_url ? <ChatImage path={form.logo_url} className="h-16 w-16 object-contain" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div>
              <input type="file" accept="image/*" ref={logoRef} onChange={uploadLogo} className="hidden" data-testid="settings-logo-input" />
              <Button type="button" variant="outline" className="rounded-full gap-1.5" onClick={() => logoRef.current?.click()} disabled={logoUp} data-testid="settings-logo-upload">
                {logoUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form.logo_url ? "Remplacer le logo" : "Téléverser un logo"}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">Affiché dans l'app, au login et sur les factures / devis.</p>
            </div>
          </div>
        </div>
        <div>
          <Label className="mb-2 block">Palette de couleurs</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {SWATCHES.map((s) => (
              <button key={s.name} onClick={() => previewColors(s.primary, s.accent)} data-testid={`swatch-${s.name}`}
                className="border border-border p-3 text-left hover:border-accent transition-colors">
                <div className="flex gap-1 mb-2"><span className="h-5 w-5" style={{ background: `hsl(${s.primary})` }} /><span className="h-5 w-5" style={{ background: `hsl(${s.accent})` }} /></div>
                <span className="text-xs">{s.name}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="space-y-1.5"><Label className="text-xs">Primaire (HSL)</Label><Input value={form.primary_color} onChange={(e) => previewColors(e.target.value, form.accent_color)} data-testid="setting-primary_color" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Accent (HSL)</Label><Input value={form.accent_color} onChange={(e) => previewColors(form.primary_color, e.target.value)} data-testid="setting-accent_color" /></div>
          </div>
        </div>
      </Section>

      <Section title="Mentions légales & facturation">
        <Field label="Adresse" k="address" form={form} set={set} />
        <Field label="N° TVA / RCI" k="vat_number" form={form} set={set} />
        <div className="space-y-1.5">
          <Label>Régime de TVA</Label>
          <Select value={form.vat_regime} onValueChange={(v) => { if (v === "assujetti" && form.vat_regime !== "assujetti") setConfirmVat(true); else set("vat_regime", v); }}>
            <SelectTrigger data-testid="setting-vat_regime"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="franchise">Franchise en base (pas de TVA)</SelectItem>
              <SelectItem value="assujetti">Assujetti à la TVA</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {form.vat_regime === "franchise"
              ? "Aucune TVA sur les factures et devis. La mention « TVA non applicable, art. 293 B du CGI » est ajoutée automatiquement sur les PDF."
              : "La TVA s'applique ligne par ligne sur les factures et devis."}
            {" "}Pensez à enregistrer. Les factures déjà créées ne sont jamais modifiées.
          </p>
        </div>
        <div className={`grid gap-3 ${form.vat_regime === "assujetti" ? "grid-cols-3" : "grid-cols-2"}`}>
          {form.vat_regime === "assujetti" && <Field label="Taux TVA (0.20)" k="default_vat_rate" type="number" form={form} set={set} />}
          <Field label="Préfixe facture" k="invoice_prefix" form={form} set={set} />
          <Field label="Préfixe devis" k="quote_prefix" form={form} set={set} />
        </div>
        <Field label="Devise" k="currency" form={form} set={set} />
        <div>
          <Field label="Mention légale de bas de facture" k="legal_mention" form={form} set={set} />
          <p className="text-xs text-muted-foreground mt-1">Pour d'éventuelles autres mentions, affichées sous le total. La mention « TVA non applicable, art. 293 B » est ajoutée automatiquement en franchise : inutile de la saisir ici. Laissez vide pour ne rien ajouter.</p>
        </div>
        {conciergerie && <div>
          <Field label="Frais de récupération à domicile (€)" k="home_pickup_fee" type="number" form={form} set={set} />
          <p className="text-xs text-muted-foreground mt-1">Ajouté en ligne séparée sur la facture quand la mission est cochée "Récupération à domicile".</p>
        </div>}
      </Section>

      <Section title="Coordonnées & règlement">
        <p className="text-xs text-muted-foreground">Imprimées sur les factures et devis PDF. Un champ laissé vide n'apparaît pas.</p>
        <Field label="Adresse de l'atelier" k="workshop_address" form={form} set={set} />
        <div className="grid grid-cols-2 gap-3"><Field label="Téléphone" k="phone" form={form} set={set} /><Field label="Email" k="email" form={form} set={set} /></div>
        {/* Transport (lot T4a) : numéro appelé par le bouton « Dispatch » des chauffeurs ; jamais imprimé sur les documents. */}
        {!conciergerie && !billingOnly && <div>
          <Field label="Téléphone du dispatch" k="dispatch_phone" form={form} set={set} />
          <p className="text-xs text-muted-foreground mt-1">{`Appelé par le bouton « Dispatch » des ${voc.employees}. Vide : le téléphone ci-dessus est utilisé.`}</p>
        </div>}
        <Field label="SIRET" k="siret" form={form} set={set} />
        <div className="border border-border rounded-xl p-4 space-y-3" data-testid="bank-block">
          <Label className="text-xs uppercase tracking-overline text-muted-foreground">Coordonnées bancaires</Label>
          <Field label="IBAN" k="iban" form={form} set={set} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="BIC" k="bic" form={form} set={set} />
            <Field label="À l'ordre de (bénéficiaire)" k="payment_payee" form={form} set={set} />
          </div>
          <p className="text-xs text-muted-foreground">Regroupées dans un bloc « Coordonnées bancaires » sur les factures. Si les trois sont vides, le bloc n'apparaît pas.</p>
        </div>
        <Field label="Modes de règlement acceptés" k="payment_methods" form={form} set={set} />
        <Field label="Conditions de règlement" k="payment_terms" form={form} set={set} />
        <div className="space-y-1.5"><Label>Message de clôture</Label>
          <Textarea rows={2} value={form.closing_message ?? ""} onChange={(e) => set("closing_message", e.target.value)} data-testid="setting-closing_message" />
        </div>
      </Section>

      {!billingOnly && <Section title={`Types de ${voc.mission}`}>
        <Textarea rows={5} value={form.mission_types_text} onChange={(e) => set("mission_types_text", e.target.value)} data-testid="setting-mission_types" />
        <p className="text-xs text-muted-foreground">Un type par ligne.</p>
      </Section>}

      {!conciergerie && !billingOnly && <Section title="Demandes spéciales">
        <p className="text-xs text-muted-foreground">{`Proposées en un clic dans la fenêtre de ${voc.mission}. L'indication (facultative) propose de préciser la demande, par exemple l'âge de l'enfant. Renommer une demande met à jour les ${voc.missions} à venir ; les ${voc.missions} passées, terminées ou annulées gardent leur libellé.`}</p>
        {form.special_requests.length > 0 && (
          <div className="space-y-2" data-testid="special-requests">
            {/* Sur téléphone, l'indication passe sous le nom (deux champs côte à côte seraient illisibles). */}
            <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_auto] gap-2 text-xs uppercase tracking-overline text-muted-foreground px-1">
              <span>Demande</span><span>Indication (facultatif)</span><span className="w-9" />
            </div>
            {form.special_requests.map((r, i) => (
              <div key={r.key || `new-${i}`} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_auto] gap-2 items-center max-sm:pb-2 max-sm:border-b max-sm:border-border" data-testid={`request-row-${i}`}>
                <Input value={r.label} maxLength={40} onChange={(e) => setRequest(i, "label", e.target.value)} placeholder="Ex. Siège bébé" data-testid={`request-label-${i}`} />
                <Input value={r.detail_hint} maxLength={60} onChange={(e) => setRequest(i, "detail_hint", e.target.value)} placeholder="Indication (facultatif)" className="max-sm:row-start-2" data-testid={`request-hint-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="max-sm:row-start-1 max-sm:col-start-2" onClick={() => rmRequest(i)} title="Retirer cette demande" data-testid={`request-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
        <Button type="button" size="sm" variant="outline" className="gap-1 rounded-full" onClick={addRequest} data-testid="request-add"><Plus className="h-3.5 w-3.5" /> Ajouter une demande</Button>
      </Section>}

      {/* Transport (lot T5b) : délais des alertes du dispatch (Patron et dispatcheurs ; la secrétaire ne les voit pas). */}
      {!conciergerie && !billingOnly && <Section title="Alertes du dispatch">
        <p className="text-xs text-muted-foreground">{`Cloche du Patron et des dispatcheurs. « Pas encore reçue » : la première des deux règles déclenche l'alerte. Une case vide coupe sa règle. Une ${voc.mission} n'alerte qu'une fois par situation.`}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="alert-settings">
          {ALERT_FIELDS.map(([k, label, bornes]) => (
            <div key={k} className="space-y-1.5 min-w-0"><Label>{label}</Label>
              <Input inputMode="decimal" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder="Vide : coupée" data-testid={`setting-${k}`} />
              <p className="text-xs text-muted-foreground">{bornes}</p>
            </div>
          ))}
        </div>
      </Section>}

      {/* Transport (lot T2) : prix des courses (Patron et dispatcheurs ; la secrétaire ne les voit pas). */}
      {!conciergerie && !billingOnly && <Section title="Prix des courses">
        <p className="text-xs text-muted-foreground">{`Le prix se fixe au cas par cas : la fenêtre de ${voc.mission} propose le dernier prix pratiqué et ces suppléments, toujours modifiables. Nuit et dimanche / jour férié ne se cumulent jamais (la plus forte).`}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="price-settings">
          <div className="space-y-1.5 min-w-0"><Label>TVA d'un transfert (%)</Label>
            <Input inputMode="decimal" value={form.vat_rate_transfer} onChange={(e) => set("vat_rate_transfer", e.target.value)} data-testid="setting-vat_rate_transfer" /></div>
          <div className="space-y-1.5 min-w-0"><Label>TVA d'une mise à disposition (%)</Label>
            <Input inputMode="decimal" value={form.vat_rate_hourly} onChange={(e) => set("vat_rate_hourly", e.target.value)} data-testid="setting-vat_rate_hourly" /></div>
        </div>
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="price-vat-warning">Taux à faire confirmer par un comptable. Ils ne s'appliquent qu'en régime « assujetti » (en franchise : TVA non applicable).</p>
        <div className="space-y-1.5"><Label>Classes de véhicule</Label>
          <Textarea rows={4} value={form.vehicle_classes_text} onChange={(e) => set("vehicle_classes_text", e.target.value)} data-testid="setting-vehicle_classes" />
          <p className="text-xs text-muted-foreground">Une classe par ligne (Berline, Van…). Un véhicule ou une course garde sa classe même si elle est retirée d'ici.</p></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 min-w-0"><Label>Nuit : de</Label>
            <Input type="time" value={form.night_start || ""} onChange={(e) => set("night_start", e.target.value)} data-testid="setting-night_start" /></div>
          <div className="space-y-1.5 min-w-0"><Label>à</Label>
            <Input type="time" value={form.night_end || ""} onChange={(e) => set("night_end", e.target.value)} data-testid="setting-night_end" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PRICE_NUMBERS.map(([k, label]) => (
            <div key={k} className="space-y-1.5 min-w-0"><Label>{label}</Label>
              <Input inputMode="decimal" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} /></div>
          ))}
        </div>
        <div className="space-y-1.5 min-w-0"><Label>Jours fériés</Label>
          <Select value={form.holiday_countries || "MC+FR"} onValueChange={(v) => set("holiday_countries", v)}>
            <SelectTrigger data-testid="setting-holiday_countries"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="MC+FR">Monaco + France</SelectItem><SelectItem value="MC">Monaco</SelectItem><SelectItem value="FR">France</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Calculés chaque année (Pâques comprise). Monaco : loi n° 798 du 18 février 1966 (report au lundi de 6 fêtes tombant un dimanche).</p></div>
        <div className="space-y-1.5"><Label>Jours fériés ajoutés à la main</Label>
          <Textarea rows={3} value={form.extra_holidays_text} onChange={(e) => set("extra_holidays_text", e.target.value)} placeholder="AAAA-MM-JJ, une date par ligne" data-testid="setting-extra_holidays" /></div>
      </Section>}

      {conciergerie && <Section title="Formules d'abonnement">
        <p className="text-xs text-muted-foreground">Proposées lors de la vente d'un abonnement (onglet Abonnements). Le nombre de visites et le prix restent modifiables à chaque vente.</p>
        {form.subscription_plans.length > 0 && (
          <div className="space-y-2">
            <div className="grid grid-cols-[minmax(0,1fr)_5rem_6rem_auto] gap-2 text-xs uppercase tracking-overline text-muted-foreground px-1">
              <span>Nom (facultatif)</span><span>Visites</span><span>Prix / visite</span><span className="w-9" />
            </div>
            {form.subscription_plans.map((p, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_5rem_6rem_auto] gap-2 items-center" data-testid={`plan-row-${i}`}>
                <Input value={p.label} onChange={(e) => setPlan(i, "label", e.target.value)} placeholder="Ex. Formule 10" data-testid={`plan-label-${i}`} />
                <Input type="number" min="1" step="1" value={p.visits} onChange={(e) => setPlan(i, "visits", e.target.value)} data-testid={`plan-visits-${i}`} />
                <Input type="number" min="0" step="0.01" value={p.unit_price} onChange={(e) => setPlan(i, "unit_price", e.target.value)} data-testid={`plan-price-${i}`} />
                <Button type="button" size="icon" variant="ghost" onClick={() => rmPlan(i)} title="Retirer cette formule" data-testid={`plan-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
        <Button type="button" size="sm" variant="outline" className="gap-1 rounded-full" onClick={addPlan} data-testid="plan-add"><Plus className="h-3.5 w-3.5" /> Ajouter une formule</Button>
      </Section>}

      {conciergerie && <Section title="Compte carburant">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Frais de service par défaut (par plein)</Label>
            <Input type="number" min="0" step="0.01" value={form.fuel_service_fee} onChange={(e) => set("fuel_service_fee", e.target.value)} data-testid="setting-fuel_service_fee" />
          </div>
          <div className="space-y-1.5"><Label>Seuil d'alerte (%)</Label>
            <Input type="number" min="0" max="100" step="1" value={form.fuel_alert_pct} onChange={(e) => set("fuel_alert_pct", e.target.value)} data-testid="setting-fuel_alert_pct" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Les frais restent modifiables à chaque plein. Une enveloppe passe « Solde bas » sous ce pourcentage du montant de sa dernière recharge.</p>
        <div className="space-y-1.5"><Label>Texte imprimé sous les débours (facture de recharge)</Label>
          <Textarea rows={2} value={form.fuel_debours_text} onChange={(e) => set("fuel_debours_text", e.target.value)} data-testid="setting-fuel_debours_text" />
          <p className="text-xs text-muted-foreground">À faire valider par votre comptable. Laissé vide, le texte par défaut est rétabli.</p>
        </div>
      </Section>}

      <Section title="Frais de société">
        <p className="text-xs text-muted-foreground">Proposés dans la saisie des dépenses (onglet Dépenses). Renommer ou retirer un élément ne modifie pas les dépenses déjà saisies.</p>
        <div className="space-y-2" data-testid="expense-categories">
          <Label>Catégories</Label>
          {form.expense_categories.map((c, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`expense-cat-row-${i}`}>
              <Input className="flex-1 min-w-0" value={c.label} onChange={(e) => setCat(i, "label", e.target.value)} placeholder="Nom de la catégorie" data-testid={`expense-cat-label-${i}`} />
              <Select value={c.color} onValueChange={(v) => setCat(i, "color", v)}>
                {/* Sur téléphone, la pastille seule : le nom de la catégorie garde la place. */}
                <SelectTrigger className="w-[4.5rem] sm:w-32 shrink-0" title="Couleur" data-testid={`expense-cat-color-${i}`}>
                  <SelectValue aria-label={expenseColor(c.color).label}>
                    <span className="inline-flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full shrink-0 ${expenseColor(c.color).dot}`} /><span className="hidden sm:inline">{expenseColor(c.color).label}</span></span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(EXPENSE_COLORS).map(([k, col]) => (
                    <SelectItem key={k} value={k}><span className="inline-flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full shrink-0 ${expenseColor(k).dot}`} />{col.label}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" size="icon" variant="ghost" className="shrink-0" onClick={() => rmCat(i)} disabled={form.expense_categories.length <= 1} title={form.expense_categories.length <= 1 ? "Gardez au moins une catégorie" : "Retirer cette catégorie"} data-testid={`expense-cat-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" className="gap-1 rounded-full" onClick={addCat} data-testid="expense-cat-add"><Plus className="h-3.5 w-3.5" /> Ajouter une catégorie</Button>
        </div>
        <div className="space-y-2" data-testid="expense-methods">
          <Label>Moyens de paiement de la société</Label>
          {form.expense_payment_methods.map((m, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`expense-method-row-${i}`}>
              <Input className="flex-1 min-w-0" value={m} onChange={(e) => setMethod(i, e.target.value)} placeholder="Ex. carte de la société" data-testid={`expense-method-label-${i}`} />
              <Button type="button" size="icon" variant="ghost" className="shrink-0" onClick={() => rmMethod(i)} disabled={form.expense_payment_methods.length <= 1} title={form.expense_payment_methods.length <= 1 ? "Gardez au moins un moyen de paiement" : "Retirer ce moyen de paiement"} data-testid={`expense-method-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" className="gap-1 rounded-full" onClick={addMethod} data-testid="expense-method-add"><Plus className="h-3.5 w-3.5" /> Ajouter un moyen de paiement</Button>
        </div>
      </Section>

      {!billingOnly && <Section title="Inscription des utilisateurs">
        <Field label="Code d'accès (requis pour créer un compte)" k="registration_code" form={form} set={set} />
        <p className="text-xs text-muted-foreground">{`Partagez ce code aux personnes autorisées. Un compte créé prend le rôle « ${voc.Employee} » (promouvable dans Utilisateurs). Laissez vide pour une inscription totalement ouverte.`}</p>
      </Section>}

      {!billingOnly && <Section title="Formulaire de réservation public">
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border">
          <span className="text-sm font-mono flex-1 min-w-0 truncate" data-testid="booking-url">{bookingUrl}</span>
          <Button type="button" size="sm" variant="outline" className="rounded-full gap-1.5" onClick={copyLink} data-testid="copy-booking-link"><Copy className="h-3.5 w-3.5" /> Copier</Button>
          <Button type="button" size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => window.open(bookingUrl, "_blank")} data-testid="open-booking-link"><ExternalLink className="h-3.5 w-3.5" /> Ouvrir</Button>
        </div>
        <div className="space-y-1">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-xs uppercase tracking-overline text-muted-foreground px-1">
            <span>Champ</span><span>Visible</span><span>Obligatoire</span>
          </div>
          {form.booking_fields.map((bf) => (
            <div key={bf.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 py-2 border-t border-border" data-testid={`booking-field-${bf.key}`}>
              <span className="text-sm">{bf.label}{(bf.key === "name" || bf.key === "email") && <span className="text-muted-foreground text-xs"> (requis pour le client)</span>}</span>
              <div className="flex justify-center w-16"><Checkbox checked={bf.enabled} onCheckedChange={(v) => setFieldFlag(bf.key, "enabled", !!v)} data-testid={`booking-enabled-${bf.key}`} /></div>
              <div className="flex justify-center w-20"><Checkbox checked={bf.required} disabled={!bf.enabled} onCheckedChange={(v) => setFieldFlag(bf.key, "required", !!v)} data-testid={`booking-required-${bf.key}`} /></div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Une réservation crée automatiquement un client « Prospect » et une demande à traiter dans l'onglet Demandes.{settings?.email_enabled && " Vous recevez aussi un e-mail à chaque demande."}</p>
      </Section>}

      <Button onClick={save} data-testid="settings-save" size="lg">Enregistrer les réglages</Button>

      {/* Indépendant du bouton « Enregistrer » ; Patron seulement. */}
      {user?.role === "master" && <BackupSection />}

      <AlertDialog open={askRemove.length > 0} onOpenChange={(o) => { if (!o) setAskRemove([]); }}>
        <AlertDialogContent data-testid="request-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{askRemove.length > 1 ? `Les enlever aussi des ${voc.missions} à venir ?` : `L'enlever aussi des ${voc.missions} à venir ?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {`${askRemove.map((r) => `« ${r.label} »`).join(", ")} ${askRemove.length > 1 ? "ont été retirées" : "a été retirée"} de la liste. Les ${voc.missions} passées, terminées ou annulées ${askRemove.length > 1 ? "les" : "la"} gardent toujours.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="request-remove-confirm-no">{askRemove.length > 1 ? "Non, les garder" : "Non, la garder"}</AlertDialogCancel>
            <AlertDialogAction onClick={removeFromUpcoming} data-testid="request-remove-confirm-yes">{askRemove.length > 1 ? "Oui, les enlever" : "Oui, l'enlever"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmVat} onOpenChange={setConfirmVat}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Passer en « Assujetti à la TVA » ?</AlertDialogTitle>
            <AlertDialogDescription>Cela ajoutera la TVA sur les nouvelles factures. Confirmer ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vat-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => set("vat_regime", "assujetti")} data-testid="vat-confirm-ok">Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
